import React from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  DataTable,
  Button,
  Input,
  Label,
  Badge,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  type Column,
} from '@redbax/ui-kit';
import { ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Save, Play, HelpCircle, CheckCircle2, XCircle, ChevronRight, Upload } from 'lucide-react';
import {
  useAdminAiService,
  useUpdateAdminAiService,
  useReplaceExtractionRules,
  useReplacePathFilters,
  useAddAiServiceDomain,
  useRemoveAiServiceDomain,
  useTestExtraction,
  useCreateAiServiceVersion,
  usePublishAiServiceVersion,
  useAiServiceVersions,
} from '../api/admin-hooks';
import type { AdminExtractionStep, AdminPathFilter, AdminAiServiceDomain, ExtractionTestResult } from '../types/admin-types';
import { formatDate, formatDateTime } from '@redbax/shared';

const STEP_TYPES = ['navigate', 'filter_array', 'filter_typed_parts', 'string_from_array', 'read_string', 'try_fields', 'parse_form_field', 'unwrap_json_string'];
const FILTER_TYPES = ['Include', 'Exclude'];

function parseParams(json: string): Record<string, unknown> {
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export function AdminAiServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { data: service, isLoading } = useAdminAiService(serviceId ?? '');
  const updateService = useUpdateAdminAiService();
  const replaceExtractionRules = useReplaceExtractionRules();
  const replacePathFilters = useReplacePathFilters();
  const addDomain = useAddAiServiceDomain();
  const removeDomain = useRemoveAiServiceDomain();
  const testExtraction = useTestExtraction();
  const createVersion = useCreateAiServiceVersion();
  const publishVersion = usePublishAiServiceVersion();
  const { data: versions } = useAiServiceVersions(serviceId ?? '');

  // Local form state
  const [name, setName] = React.useState('');
  const [provider, setProvider] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [isEnabled, setIsEnabled] = React.useState(true);
  const [steps, setSteps] = React.useState<AdminExtractionStep[]>([]);
  const [filters, setFilters] = React.useState<AdminPathFilter[]>([]);

  // Dialog state
  const [newDomain, setNewDomain] = React.useState('');
  const [showAddDomain, setShowAddDomain] = React.useState(false);
  const [showAddStep, setShowAddStep] = React.useState(false);
  const [newStepType, setNewStepType] = React.useState(STEP_TYPES[0]);
  const [newStepParams, setNewStepParams] = React.useState('{}');
  const [showAddFilter, setShowAddFilter] = React.useState(false);
  const [newFilterPattern, setNewFilterPattern] = React.useState('');
  const [newFilterType, setNewFilterType] = React.useState(FILTER_TYPES[0]);
  const [saving, setSaving] = React.useState(false);
  const [lastSavedVersionId, setLastSavedVersionId] = React.useState<number | null>(null);
  const [testInput, setTestInput] = React.useState('');
  const [testResult, setTestResult] = React.useState<ExtractionTestResult | null>(null);
  const [showHelp, setShowHelp] = React.useState(false);

  // Sync server data to local state only on initial load
  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (service && !initialized.current) {
      initialized.current = true;
      setName(service.name);
      setProvider(service.provider ?? '');
      setCategory(service.category);
      setIsEnabled(service.isEnabled);
      setSteps([...service.extractionSteps].sort((a, b) => a.stepOrder - b.stepOrder));
      setFilters([...service.pathFilters]);
    }
  }, [service]);

  if (isLoading || !service) {
    return (
      <div>
        <PageHeader title="Loading..." />
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-surface-raised" />
          ))}
        </div>
      </div>
    );
  }

  const handleSaveDraft = async () => {
    if (!serviceId) return;
    setSaving(true);
    try {
      await updateService.mutateAsync({
        id: serviceId,
        name,
        provider: provider || null,
        category,
        isEnabled,
      });
      const result = await createVersion.mutateAsync({ id: serviceId, steps, filters });
      setLastSavedVersionId(result.versionId);
      initialized.current = false;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (versionId: number) => {
    if (!serviceId) return;
    await publishVersion.mutateAsync({ id: serviceId, versionId });
  };

  const handleAddDomain = () => {
    if (!serviceId || !newDomain.trim()) return;
    addDomain.mutate({ id: serviceId, domain: newDomain.trim() });
    setNewDomain('');
    setShowAddDomain(false);
  };

  const handleRemoveDomain = (domainId: string) => {
    if (!serviceId) return;
    removeDomain.mutate({ id: serviceId, domainId });
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down') => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSteps.length) return;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    setSteps(newSteps.map((s, i) => ({ ...s, stepOrder: i + 1 })));
  };

  const handleRemoveStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    setSteps(newSteps.map((s, i) => ({ ...s, stepOrder: i + 1 })));
  };

  const handleAddStep = () => {
    setSteps([
      ...steps,
      {
        stepOrder: steps.length + 1,
        stepType: newStepType,
        parametersJson: newStepParams,
      },
    ]);
    setNewStepType(STEP_TYPES[0]);
    setNewStepParams('{}');
    setShowAddStep(false);
  };

  const handleAddFilter = () => {
    if (!newFilterPattern.trim()) return;
    setFilters([...filters, { pattern: newFilterPattern.trim(), filterType: newFilterType }]);
    setNewFilterPattern('');
    setNewFilterType(FILTER_TYPES[0]);
    setShowAddFilter(false);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleTestExtraction = async () => {
    if (!testInput.trim()) return;
    setTestResult(null);
    try {
      const result = await testExtraction.mutateAsync({ body: testInput, steps });
      setTestResult(result);
    } catch {
      setTestResult({ extractedText: null, success: false, error: 'Request failed', trace: [] });
    }
  };

  const domainColumns: Column<AdminAiServiceDomain>[] = [
    { key: 'domain', header: 'Domain', sortable: true },
    {
      key: 'createdAt',
      header: 'Added',
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'actions' as keyof AdminAiServiceDomain,
      header: '',
      render: (row) => (
        <Button size="sm" variant="ghost" onClick={() => handleRemoveDomain(row.id)}>
          <Trash2 className="h-4 w-4 text-red-accent" />
        </Button>
      ),
    },
  ];

  const filterColumns: Column<AdminPathFilter & { _idx: number }>[] = [
    { key: 'pattern', header: 'Pattern', sortable: true },
    { key: 'filterType', header: 'Type', sortable: true },
    {
      key: 'actions' as keyof (AdminPathFilter & { _idx: number }),
      header: '',
      render: (row) => (
        <Button size="sm" variant="ghost" onClick={() => handleRemoveFilter(row._idx)}>
          <Trash2 className="h-4 w-4 text-red-accent" />
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={service.name}
        description={`${service.category}${service.provider ? ` — ${service.provider}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="info">
              v{service.publishedVersionId ?? 'none'}
            </Badge>
            <Button variant="outline" onClick={() => navigate('/ai-services')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            {lastSavedVersionId && (
              <Button onClick={() => handlePublish(lastSavedVersionId)} disabled={publishVersion.isPending}>
                <Upload className="mr-2 h-4 w-4" />
                {publishVersion.isPending ? 'Publishing...' : `Publish v${lastSavedVersionId}`}
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        {/* Service Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Service Info</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="service-name">Name</Label>
                <Input
                  id="service-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-provider">Provider</Label>
                <Input
                  id="service-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-category">Category</Label>
                <Input
                  id="service-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </div>
              <div className="flex items-end gap-3">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isEnabled}
                      onClick={() => setIsEnabled(!isEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isEnabled ? 'bg-green-ok' : 'bg-surface-raised'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          isEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-sm text-text-secondary">
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Version History Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Version History</CardTitle>
          </CardHeader>
          <CardContent>
            {!versions || versions.length === 0 ? (
              <div className="flex h-16 items-center justify-center text-sm text-text-secondary">
                No versions yet
              </div>
            ) : (
              <div className="space-y-2">
                {versions.map((v) => (
                  <div
                    key={v.versionId}
                    className="flex items-center justify-between rounded-lg border border-border bg-surface-raised p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-medium text-text-primary">
                        v{v.versionId}
                      </span>
                      <Badge
                        variant={
                          v.status === 'Published' ? 'default' :
                          v.status === 'Draft' ? 'info' : 'secondary'
                        }
                      >
                        {v.status}
                      </Badge>
                      <span className="text-xs text-text-secondary">
                        {formatDateTime(v.createdAt)}
                      </span>
                      {v.publishedAt && (
                        <span className="text-xs text-text-secondary">
                          Published {formatDateTime(v.publishedAt)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-secondary">
                        {v.extractionSteps.length} steps, {v.pathFilters.length} filters
                      </span>
                      {v.status === 'Draft' && (
                        <Button
                          size="sm"
                          onClick={() => handlePublish(v.versionId)}
                          disabled={publishVersion.isPending}
                        >
                          <Upload className="mr-1 h-3 w-3" />
                          Publish
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSteps([...v.extractionSteps].sort((a, b) => a.stepOrder - b.stepOrder));
                          setFilters([...v.pathFilters]);
                        }}
                      >
                        Load
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Domains Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Domains</CardTitle>
              <Button size="sm" onClick={() => setShowAddDomain(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Domain
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={domainColumns}
              data={service.domains}
              keyExtractor={(d) => d.id}
              emptyMessage="No domains configured"
            />
          </CardContent>
        </Card>

        {/* Extraction Pipeline Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Extraction Pipeline</CardTitle>
              <Button size="sm" onClick={() => setShowAddStep(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Step
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {steps.length === 0 ? (
              <div className="flex h-20 items-center justify-center text-sm text-text-secondary">
                No extraction steps configured
              </div>
            ) : (
              <div className="space-y-3">
                {steps.map((step, idx) => {
                  const params = parseParams(step.parametersJson);
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 rounded-lg border border-border bg-surface-raised p-4"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <button
                          className="rounded p-1 text-text-secondary hover:bg-surface hover:text-text-primary disabled:opacity-30"
                          onClick={() => handleMoveStep(idx, 'up')}
                          disabled={idx === 0}
                          aria-label="Move step up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-subtle text-xs font-bold text-red-accent">
                          {step.stepOrder}
                        </span>
                        <button
                          className="rounded p-1 text-text-secondary hover:bg-surface hover:text-text-primary disabled:opacity-30"
                          onClick={() => handleMoveStep(idx, 'down')}
                          disabled={idx === steps.length - 1}
                          aria-label="Move step down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="info">{step.stepType}</Badge>
                        </div>
                        {Object.keys(params).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {Object.entries(params).map(([key, value]) => (
                              <div key={key} className="flex items-baseline gap-2 text-xs">
                                <span className="font-medium text-text-secondary">{key}:</span>
                                <span className="text-text-primary">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveStep(idx)}
                      >
                        <Trash2 className="h-4 w-4 text-red-accent" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Extraction Help Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                <button
                  type="button"
                  className="flex items-center gap-2 hover:text-text-primary"
                  onClick={() => setShowHelp(!showHelp)}
                >
                  <HelpCircle className="h-4 w-4" />
                  Step Type Reference
                  <ChevronRight className={`h-4 w-4 transition-transform ${showHelp ? 'rotate-90' : ''}`} />
                </button>
              </CardTitle>
            </div>
          </CardHeader>
          {showHelp && (
            <CardContent>
              <div className="space-y-4 text-sm text-text-secondary">
                <p>
                  The extraction pipeline processes a request body through an ordered sequence of steps.
                  Each step receives the output of the previous step and either passes a JSON element forward
                  or extracts a final text string. If the last step leaves a string element, it is used automatically.
                </p>
                <div className="space-y-3">
                  <div>
                    <Badge variant="info">parse_form_field</Badge>
                    <span className="ml-2">Extract a named field from a form-encoded body and parse as JSON. Must be step 1.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{"field": "f.req"}  — extracts the f.req field, URL-decodes, parses as JSON`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">navigate</Badge>
                    <span className="ml-2">Navigate into a JSON element by dot-separated path. Supports array indices.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{"path": "messages"}    — access root.messages (object property)
{"path": "0.0"}         — access root[0][0] (array indices)
{"path": "data.0.text"} — mixed: root.data[0].text`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">unwrap_json_string</Badge>
                    <span className="ml-2">Parse a JSON string element that contains embedded JSON (double-encoded). No parameters.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{}  — if current element is "[1,2,3]" (string), parses to [1,2,3] (array)`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">filter_array</Badge>
                    <span className="ml-2">Find the last array item matching a property value (case-insensitive).</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{"property": "role", "value": "user"}
{"property": "author.role", "value": "user"}  — dotted paths supported`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">read_string</Badge>
                    <span className="ml-2">Read text from a property. Handles plain strings, typed part arrays, and <code>parts</code> arrays.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{"property": "content"}  — reads from .content (string, typed parts, or {parts:[...]})`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">try_fields</Badge>
                    <span className="ml-2">Try multiple field names in order, return the first non-empty value.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{"fields": ["prompt", "message", "content", "query", "input"]}`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">filter_typed_parts</Badge>
                    <span className="ml-2">Filter an array of typed objects, extracting text from matching items.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{"type_property": "type", "type_value": "text", "text_property": "text"}`}</pre>
                  </div>
                  <div>
                    <Badge variant="info">string_from_array</Badge>
                    <span className="ml-2">Extract the last string from a string array. No parameters needed.</span>
                    <pre className="mt-1 rounded bg-surface-raised px-3 py-2 text-xs font-mono text-text-primary">
{`{}`}</pre>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="rounded border border-border bg-surface-raised p-3">
                    <p className="font-medium text-text-primary">Pattern: Messages API (ChatGPT, Claude, etc.)</p>
                    <p className="mt-1">Standard JSON body with messages array:</p>
                    <ol className="mt-2 list-inside list-decimal space-y-1 font-mono text-xs">
                      <li><Badge variant="info">navigate</Badge> <code>{`{"path":"messages"}`}</code></li>
                      <li><Badge variant="info">filter_array</Badge> <code>{`{"property":"role","value":"user"}`}</code></li>
                      <li><Badge variant="info">read_string</Badge> <code>{`{"property":"content"}`}</code></li>
                    </ol>
                  </div>
                  <div className="rounded border border-border bg-surface-raised p-3">
                    <p className="font-medium text-text-primary">Pattern: Form-encoded (Gemini Web UI)</p>
                    <p className="mt-1">Form-encoded body with double-encoded JSON in <code>f.req</code> field:</p>
                    <ol className="mt-2 list-inside list-decimal space-y-1 font-mono text-xs">
                      <li><Badge variant="info">parse_form_field</Badge> <code>{`{"field":"f.req"}`}</code></li>
                      <li><Badge variant="info">navigate</Badge> <code>{`{"path":"1"}`}</code></li>
                      <li><Badge variant="info">unwrap_json_string</Badge> <code>{`{}`}</code></li>
                      <li><Badge variant="info">navigate</Badge> <code>{`{"path":"0.0"}`}</code></li>
                    </ol>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Extraction Tester Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Extraction Tester</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-sm text-text-secondary">
              Paste a sample request body below (JSON or form-encoded) and run the current extraction pipeline against it.
              The tester uses the steps shown above (including any unsaved changes).
            </p>
            <div className="space-y-3">
              <textarea
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                placeholder={`{\n  "messages": [\n    {"role": "user", "content": "Hello, world!"}\n  ]\n}`}
                rows={8}
                className="w-full rounded-md border border-border bg-surface-raised px-3 py-2 font-mono text-sm text-text-primary placeholder:text-text-secondary/50 focus:border-red-accent focus:outline-none focus:ring-1 focus:ring-red-accent"
              />
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleTestExtraction}
                  disabled={testExtraction.isPending || !testInput.trim() || steps.length === 0}
                >
                  <Play className="mr-2 h-4 w-4" />
                  {testExtraction.isPending ? 'Running...' : 'Run Extraction'}
                </Button>
                {steps.length === 0 && (
                  <span className="text-sm text-text-secondary">Add extraction steps above first.</span>
                )}
              </div>

              {testResult && (
                <div className="space-y-4 rounded-lg border border-border p-4">
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-ok" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-accent" />
                    )}
                    <span className="font-medium text-text-primary">
                      {testResult.success ? 'Extraction Successful' : 'Extraction Failed'}
                    </span>
                  </div>

                  {testResult.trace.length > 0 && (
                    <div className="space-y-3">
                      {testResult.trace.map((t, i) => (
                        <div
                          key={i}
                          className={`rounded-lg border p-3 ${
                            t.status === 'fail'
                              ? 'border-red-accent/30 bg-red-subtle'
                              : t.status === 'text'
                                ? 'border-green-ok/30 bg-green-ok/10'
                                : 'border-border bg-surface-raised'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface text-xs font-bold text-text-secondary">
                              {t.stepOrder}
                            </span>
                            <Badge variant={t.status === 'fail' ? 'destructive' : t.status === 'text' ? 'default' : 'info'}>
                              {t.stepType}
                            </Badge>
                            {t.status === 'text' && (
                              <span className="text-xs font-medium text-green-ok">final result</span>
                            )}
                            {t.status === 'fail' && (
                              <span className="text-xs font-medium text-red-accent">failed</span>
                            )}
                          </div>
                          {t.error && (
                            <pre className="max-h-32 overflow-auto rounded bg-red-subtle/50 px-3 py-2 text-xs font-mono text-red-accent whitespace-pre-wrap">
                              {t.error}
                            </pre>
                          )}
                          {t.output && (
                            <pre className="max-h-48 overflow-auto rounded bg-surface px-3 py-2 text-xs font-mono text-text-primary whitespace-pre-wrap break-all">
                              {t.output}
                            </pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {testResult.error && testResult.trace.length === 0 && (
                    <div className="rounded bg-red-subtle px-3 py-2 text-sm text-red-accent">
                      {testResult.error}
                    </div>
                  )}

                  {testResult.extractedText && (
                    <div className="rounded-lg border border-green-ok/30 bg-green-ok/10 p-3">
                      <Label className="text-xs text-green-ok">Extracted Text</Label>
                      <pre className="mt-1 max-h-40 overflow-auto rounded bg-surface px-3 py-2 text-sm font-mono text-text-primary whitespace-pre-wrap">
                        {testResult.extractedText}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Path Filters Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Path Filters</CardTitle>
              <Button size="sm" onClick={() => setShowAddFilter(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Filter
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={filterColumns}
              data={filters.map((f, i) => ({ ...f, _idx: i }))}
              keyExtractor={(f) => `${f.pattern}-${f._idx}`}
              emptyMessage="No path filters configured"
            />
          </CardContent>
        </Card>
      </div>

      {/* Add Domain Dialog */}
      {showAddDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowAddDomain(false)} />
          <div className="relative z-50 w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Add Domain</h3>
            <div className="mt-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="new-domain">Domain</Label>
                <Input
                  id="new-domain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="e.g. api.openai.com"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddDomain(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddDomain} disabled={!newDomain.trim()}>
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Step Dialog */}
      {showAddStep && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowAddStep(false)} />
          <div className="relative z-50 w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Add Extraction Step</h3>
            <div className="mt-4 space-y-3">
              <div className="space-y-2">
                <Label>Step Type</Label>
                <Select value={newStepType} onValueChange={setNewStepType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STEP_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="step-params">Parameters (JSON)</Label>
                <Input
                  id="step-params"
                  value={newStepParams}
                  onChange={(e) => setNewStepParams(e.target.value)}
                  placeholder='{}'
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddStep(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddStep}>
                Add Step
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Filter Dialog */}
      {showAddFilter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowAddFilter(false)} />
          <div className="relative z-50 w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Add Path Filter</h3>
            <div className="mt-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="filter-pattern">Pattern</Label>
                <Input
                  id="filter-pattern"
                  value={newFilterPattern}
                  onChange={(e) => setNewFilterPattern(e.target.value)}
                  placeholder="e.g. /v1/chat/completions"
                />
              </div>
              <div className="space-y-2">
                <Label>Filter Type</Label>
                <Select value={newFilterType} onValueChange={setNewFilterType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddFilter(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddFilter} disabled={!newFilterPattern.trim()}>
                Add Filter
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
